import { User } from 'firebase/auth';
import {
  IngredientSummary,
  IngredientRecord,
  IngredientRecipes,
} from '../types/ingredient';
import { PantryCategory } from '../types/pantry';

const API_URL = process.env.REACT_APP_API_URL;

const getHeaders = async (user: User) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${await user.getIdToken()}`,
});

// All ingredient summaries — loaded once; the browser searches/paginates them.
export const fetchIngredientSummaries = async (
  user: User
): Promise<IngredientSummary[]> => {
  const response = await fetch(`${API_URL}/api/admin/ingredients`, {
    headers: await getHeaders(user),
  });
  if (!response.ok) throw new Error('Failed to fetch ingredients');
  const data = await response.json();
  return data.ingredients;
};

export const fetchIngredient = async (
  user: User,
  name: string
): Promise<IngredientRecord> => {
  const response = await fetch(
    `${API_URL}/api/admin/ingredients/${encodeURIComponent(name)}`,
    { headers: await getHeaders(user) }
  );
  if (response.status === 404) throw new Error('Ingredient not found');
  if (!response.ok) throw new Error('Failed to fetch ingredient');
  const data = await response.json();
  return data.ingredient;
};

// User + catalog recipes that use this ingredient (reverse index lookup).
export const fetchIngredientRecipes = async (
  user: User,
  name: string
): Promise<IngredientRecipes> => {
  const response = await fetch(
    `${API_URL}/api/admin/ingredients/${encodeURIComponent(name)}/recipes`,
    { headers: await getHeaders(user) }
  );
  if (!response.ok) throw new Error('Failed to fetch recipes for ingredient');
  return response.json();
};

// Manually override an ingredient's category and/or staple flag (admin). The
// record is marked classificationSource "admin" server-side so it sticks.
export const updateIngredientClassification = async (
  user: User,
  name: string,
  update: { category?: PantryCategory; isStaple?: boolean }
): Promise<IngredientRecord> => {
  const response = await fetch(
    `${API_URL}/api/admin/ingredients/${encodeURIComponent(name)}/classification`,
    {
      method: 'PATCH',
      headers: await getHeaders(user),
      body: JSON.stringify(update),
    }
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to update classification');
  }
  const data = await response.json();
  return data.ingredient;
};

// Full Kroger refresh of one ingredient (re-search, re-pick, re-price, re-image).
// Synchronous — returns the updated record. Spends Kroger + AI budget.
export const refreshIngredient = async (
  user: User,
  name: string
): Promise<IngredientRecord> => {
  const response = await fetch(
    `${API_URL}/api/admin/ingredients/${encodeURIComponent(name)}/refresh`,
    { method: 'POST', headers: await getHeaders(user) }
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to refresh ingredient');
  }
  const data = await response.json();
  return data.ingredient;
};
